import React, { useState, useEffect } from "react";
import { useAppContext } from "../../functions/ContextAPI";
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  FolderOpenIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import DocumentPreview from "../../components/DocumentPreview";

/**
 * NUC Documents Page Component
 * Displays official NUC accreditation documents (self-study and evaluation forms)
 */
const NUCDocuments = () => {
  const context = useAppContext();
  const [documents, setDocuments] = useState([]);
  const [expectedDocuments, setExpectedDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const currentProgramme = context.selectedProgramme;
  const accreditationId = currentProgramme?.accreditationId || currentProgramme?._id;

  // Fetch accreditation documents on mount
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!accreditationId) {
        return;
      }

      setIsLoading(true);
      try {
        const { getAccreditationDocuments } = await import("../../service/templateService");
        const data = await getAccreditationDocuments(accreditationId);

        // Convert to array format for display
        const docsArray = [];

        if (data.selfStudyForm) {
          const doc = data.selfStudyForm;
          docsArray.push({
            id: doc._id,
            name: doc.name || "Self-Study Form",
            type: doc.fileType?.split('/').pop()?.toUpperCase() || "PDF",
            description: doc.description,
            category: doc.category,
            uploadPath: doc.uploadPath || doc.filePath,
            fileName: doc.fileName,
            size: doc.fileSize,
            isUploaded: doc.isUploaded,
            uploadedDate: doc.uploadDate,
          });
        }

        if (data.evaluationForm) {
          const doc = data.evaluationForm;
          docsArray.push({
            id: doc._id,
            name: doc.name || "Programme Evaluation Form",
            type: doc.fileType?.split('/').pop()?.toUpperCase() || "PDF",
            description: doc.description,
            category: doc.category,
            uploadPath: doc.uploadPath || doc.filePath,
            fileName: doc.fileName,
            size: doc.fileSize,
            isUploaded: doc.isUploaded,
            uploadedDate: doc.uploadDate,
          });
        }

        setDocuments(docsArray);

        // Set expected documents
        if (data.expectedDocuments && Array.isArray(data.expectedDocuments)) {
          setExpectedDocuments(data.expectedDocuments);
        }
      } catch (error) {
        console.error("Error fetching accreditation documents:", error);
        toast.error("Failed to load accreditation documents");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [accreditationId]);

  const handlePreview = (document) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  const handleDownload = (document) => {
    if (document.uploadPath) {
      // Create download URL from backend
      const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const downloadUrl = `${apiBaseUrl}/download/${document.uploadPath}`;

      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = document.name;
      link.click();

      toast.success(`Downloading ${document.name}...`);
    } else {
      toast.warning("Document file not available for download");
    }
  };

  if (!currentProgramme) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-yellow-50 rounded-lg border-2 border-yellow-200 p-8 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              No Programme Selected
            </h3>
            <p className="text-yellow-800">
              Please select a programme to view NUC accreditation documents.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-blue-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">NUC Documents</h1>
              <p className="text-gray-600 mt-1">
                Official NUC accreditation forms and documents
              </p>
            </div>
          </div>

          {/* Selected Programme Info */}
          {currentProgramme && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm font-semibold text-blue-900">
                Current Programme: {currentProgramme.programmeName || "N/A"}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {currentProgramme.institutionName || "N/A"}
              </p>
            </div>
          )}
        </div>

        {/* Documents Section */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Accreditation Forms</h2>
              <p className="text-sm text-gray-600 mt-1">
                Self-study and programme evaluation forms for {currentProgramme.programmeName}
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading documents...</p>
              </div>
            </div>
          ) : documents.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {documents.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-12 text-center">
              <FolderOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Available</h3>
              <p className="text-gray-600">There are no NUC accreditation documents available for this programme.</p>
            </div>
          )}
        </div>

        {/* Expected Documents Section */}
        {expectedDocuments.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Expected Documents</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Documents that should be uploaded for accreditation ({expectedDocuments.length} total)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {expectedDocuments.map((expectedDoc) => (
                <ExpectedDocumentCard
                  key={expectedDoc._id || expectedDoc.expectedId}
                  document={{
                    name: expectedDoc.title,
                    description: expectedDoc.description,
                    category: expectedDoc.category,
                    required: expectedDoc.required,
                    isUploaded: expectedDoc.isUploaded,
                    uploadDate: expectedDoc.uploadDate,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Document Preview Modal */}
        {showPreview && selectedDocument && (
          <DocumentPreview
            isOpen={showPreview}
            onClose={() => {
              setShowPreview(false);
              setSelectedDocument(null);
            }}
            document={{
              name: selectedDocument.name,
              description: selectedDocument.description,
              uri: selectedDocument.uploadPath
                ? `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/download/${selectedDocument.uploadPath}`
                : null,
              type: selectedDocument.type?.toLowerCase(),
            }}
            downloadUrl={
              selectedDocument.uploadPath
                ? `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/download/${selectedDocument.uploadPath}`
                : null
            }
          />
        )}
      </div>
    </div>
  );
};

/**
 * Document Card Component
 * Displays individual document information
 */
const DocumentCard = ({ document, onDownload, onPreview }) => {
  const getCategoryColor = (category) => {
    const colors = {
      "Self-Study": "bg-purple-100 text-purple-800 border-purple-200",
      Evaluation: "bg-green-100 text-green-800 border-green-200",
    };
    return colors[category] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-base font-semibold text-gray-900">{document.name}</h3>
              {document.category && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${getCategoryColor(
                    document.category
                  )}`}
                >
                  {document.category}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-3">{document.description}</p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span className="flex items-center space-x-1">
                <span className="font-medium">Type:</span>
                <span>{document.type}</span>
              </span>
              {document.size && (
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Size:</span>
                  <span>{document.size}</span>
                </span>
              )}
              {document.uploadedDate && (
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Date:</span>
                  <span>{new Date(document.uploadedDate).toLocaleDateString()}</span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          {/* Preview Button */}
          {onPreview && document.uploadPath && (
            <button
              onClick={() => onPreview(document)}
              className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border-2 border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all duration-200 flex items-center space-x-2"
            >
              <EyeIcon className="h-4 w-4" />
              <span>Preview</span>
            </button>
          )}
          {/* Download Button */}
          <button
            onClick={() => onDownload(document)}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 flex items-center space-x-2"
            disabled={!document.uploadPath && !document.isUploaded}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Expected Document Card Component
 * Displays expected documents with status indicator
 */
const ExpectedDocumentCard = ({ document }) => {
  const getCategoryColor = (category) => {
    const colors = {
      Academic: "bg-blue-100 text-blue-800 border-blue-200",
      Staffing: "bg-green-100 text-green-800 border-green-200",
      Facilities: "bg-purple-100 text-purple-800 border-purple-200",
      Library: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Administration: "bg-indigo-100 text-indigo-800 border-indigo-200",
      Quality: "bg-pink-100 text-pink-800 border-pink-200",
      General: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[category] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const isUploaded = document.isUploaded || false;

  return (
    <div className={`bg-white border-2 rounded-lg p-5 ${isUploaded ? 'border-green-300 opacity-100' : 'border-gray-300 opacity-75'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className="flex-shrink-0">
            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br flex items-center justify-center ${
              isUploaded ? 'from-green-400 to-green-500' : 'from-gray-400 to-gray-500'
            }`}>
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-base font-semibold text-gray-900">{document.name}</h3>
              {document.category && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${getCategoryColor(
                    document.category
                  )}`}
                >
                  {document.category}
                </span>
              )}
              {document.required && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                  Required
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-3">{document.description}</p>
            <div className="flex items-center space-x-4 text-xs">
              {isUploaded ? (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-300">
                  Uploaded {document.uploadDate && `on ${new Date(document.uploadDate).toLocaleDateString()}`}
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-300">
                  Not Uploaded
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NUCDocuments;
